import { combineDateTime, toDateInput, toTimeInput } from '@/lib/activityTime';

// A centre visit as staff type it: one day, an arrival time and, unless the
// visit is still going on today, a leaving time. The API checks the same
// rules; checking them here too answers before the round trip.

/** A participant's visit, as GET /api/participants/{id}/visits returns it. */
export interface StaffVisit {
  id: number;
  participantId: number;
  digitalCenterId: number;
  digitalCenterName: string;
  checkedInAt: string;
  checkedOutAt: string | null;
  /** Who checked them in; null when they did it themselves. */
  checkedInByName: string | null;
  /** Who checked them out; null when they did, or closing time did. */
  checkedOutByName: string | null;
}

/** The body of POST /api/participants/{id}/visits and PUT /api/centro-visits/{id}. */
export interface VisitInput {
  digitalCenterId: number;
  checkedInAt: string;
  checkedOutAt: string | null;
}

export interface VisitForm {
  digitalCenterId: number | null;
  date: string;
  checkIn: string;
  checkOut: string;
}

export type VisitFormErrors = Partial<Record<keyof VisitForm, string>>;

/** A new visit: today, at the participant's usual centre when there is one. */
export function emptyVisitForm(digitalCenterId: number | null, now = new Date()): VisitForm {
  return { digitalCenterId, date: toDateInput(now), checkIn: '', checkOut: '' };
}

/** The form for correcting a visit that exists. */
export function visitFormFrom(visit: StaffVisit): VisitForm {
  const checkedIn = new Date(visit.checkedInAt);
  return {
    digitalCenterId: visit.digitalCenterId,
    date: toDateInput(checkedIn),
    checkIn: toTimeInput(checkedIn),
    checkOut: visit.checkedOutAt ? toTimeInput(new Date(visit.checkedOutAt)) : '',
  };
}

/** Whether the visit's day is today, the only day a visit may stay open. */
export function isToday(date: string, now = new Date()): boolean {
  return date === toDateInput(now);
}

export function validateVisitForm(form: VisitForm, now = new Date()): VisitFormErrors {
  const errors: VisitFormErrors = {};
  if (!form.digitalCenterId) errors.digitalCenterId = 'O Centro Digital é obrigatório.';

  if (!form.date) {
    errors.date = 'O dia é obrigatório.';
    return errors;
  }
  if (form.date > toDateInput(now)) {
    errors.date = 'O dia não pode ser no futuro.';
    return errors;
  }

  const checkedIn = combineDateTime(form.date, form.checkIn);
  if (!form.checkIn) errors.checkIn = 'A hora de entrada é obrigatória.';
  else if (!checkedIn) errors.checkIn = 'Hora de entrada inválida.';
  else if (checkedIn > now) errors.checkIn = 'A entrada não pode ser no futuro.';

  if (!form.checkOut) {
    if (!isToday(form.date, now)) errors.checkOut = 'Indica a hora de saída.';
    return errors;
  }
  const checkedOut = combineDateTime(form.date, form.checkOut);
  if (!checkedOut) errors.checkOut = 'Hora de saída inválida.';
  else if (checkedOut > now) errors.checkOut = 'A saída não pode ser no futuro.';
  else if (checkedIn && checkedOut <= checkedIn) errors.checkOut = 'A saída tem de ser depois da entrada.';
  return errors;
}

/** The request body. Call only on a form validateVisitForm accepted. */
export function toVisitInput(form: VisitForm): VisitInput {
  const checkedIn = combineDateTime(form.date, form.checkIn);
  const checkedOut = form.checkOut ? combineDateTime(form.date, form.checkOut) : null;
  return {
    digitalCenterId: form.digitalCenterId ?? 0,
    checkedInAt: checkedIn ? checkedIn.toISOString() : '',
    checkedOutAt: checkedOut ? checkedOut.toISOString() : null,
  };
}

/** The API's errors by field, put on the form's fields. */
export function visitServerErrors(errors: Record<string, string>): VisitFormErrors {
  return {
    digitalCenterId: errors.digitalCenterId,
    checkIn: errors.checkedInAt,
    checkOut: errors.checkedOutAt,
  };
}

const TIME: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

/** "10:00–12:30", or "10:00 – em curso" for a visit still open. */
export function formatVisitTimes(visit: Pick<StaffVisit, 'checkedInAt' | 'checkedOutAt'>): string {
  const start = new Date(visit.checkedInAt).toLocaleTimeString('pt-PT', TIME);
  if (!visit.checkedOutAt) return `${start} – em curso`;
  return `${start}–${new Date(visit.checkedOutAt).toLocaleTimeString('pt-PT', TIME)}`;
}
